import * as $ from "jquery";
import { keyBy } from "lodash";
import Promise = JQuery.Promise;

export type CacheData<T> = {
    [key: string]: T;
};

export abstract class EntityCache<EntityInfo> {
    cachedData: CacheData<EntityInfo> = null;
    private _$req: JQuery.jqXHR;
    abstract keyName: keyof EntityInfo;
    abstract getAllUrl: string;
    static _instance: any;

    ensureLoad() {
        if (this._$req) {
            return this._$req;
        }

        return (this._$req = this.getAll().done((result: EntityInfo[]) => {
            this.cachedData = keyBy(result, this.keyName);
        }));
    }

    getData(): PromiseLike<EntityInfo[]> {
        return this.ensureLoad().then(() => Object.values(this.cachedData));
    }

    protected getAll() {
        return ep.common.get(this.getAllUrl, null, null, { skipPostProcess: true });
    }

    get(id: number): EntityInfo {
        this._ensureLoaded(); // just in case - should be called only after server response on init

        return this.cachedData[id];
    }

    tryGet(id: number): EntityInfo {
        if (!this.cachedData) {
            return null;
        }

        return this.get(id);
    }

    add(data: EntityInfo): void {
        this._ensureLoaded();

        const id: any = data[this.keyName];
        this.cachedData[id] = data;
    }

    _ensureLoaded() {
        if (!this.cachedData) {
            this.throwDataNotLoaded();
        }
    }

    load = (ids: number[], taskId?: number): Promise<EntityInfo[]> => {
        if (ids.length) {
            return this.loadEntities(ids, taskId).done(this._mergeEntities);
        }
        return this._getEmptyResult();
    };

    protected _mergeEntities = (data: EntityInfo[]) => {
        this.cachedData = { ...this.cachedData, ...keyBy(data, this.keyName) };
    };

    protected _getEmptyResult() {
        return $.Deferred()
            .resolve([] as EntityInfo[])
            .promise();
    }

    protected abstract loadEntities(ids: number[], taskId?: number): Promise<EntityInfo[]>;

    public preloadEntities = (ids: number[], taskId?: number): PromiseLike<any> => {
        return this.ensureLoad().then(() => {
            const missedIds = ids.filter(id => !this.get(id));
            return this.load(missedIds, taskId);
        });
    };

    protected abstract throwDataNotLoaded(): void;

    static reset() {
        this._instance = null;
    }
}
